import useTranslator from "../../hooks/useTranslator";
import { useContext, useEffect, useRef, useState } from "react";
import {
  haveValue,
  isDefaultWorkspace,
  shouldShowCapacityReport,
} from "../../helper/common";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import ConfirmWorkspaceRemovalModal from "./ConfirmWorkspaceRemovalModal";
import ManageMembersModal from "./ManageMembersModal";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import InvitationsCustomization from "./InvitationsCustomization";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";
import NotificationCustomization from "./NotificationCustomization";
import BillingCustomization from "./BillingCustomization";
import EditBillingModal from "./EditBillingModal";
import EditBeneficiaryModal from "./EditBeneficiaryModal";
import {
  FileInput,
  RowButton,
  Button,
  Field,
  Icon,
  Modal as DsModal,
  Tabs,
  TextField,
} from "../design";
import "./workspaceModals.css";

const DEFAULT_EFFORT_RATE = 35;
const MIN_EFFORT_RATE = 10;
const MAX_EFFORT_RATE = 50;

const clampEffortRate = (value) =>
  Math.min(
    Math.max(Number(value) || DEFAULT_EFFORT_RATE, MIN_EFFORT_RATE),
    MAX_EFFORT_RATE
  );

const CreateNewWorkspaceModal = (props) => {
  const {
    workspaceTab = "settings",
    changeWorkspaceTab,
    isOpen,
    onClose,
    workspace = null,
    setOpenWorkspaceModal,
    // setOpenInviteModal = false,
    setOpenInvitationCustomization = false,
    setWorkspaceName,
    workspaceName,
    actionType = "add",
    // handleSubmitEdit,
    // isModalButtonLoading,
    setWorkspaceImage,
    defaultEffortRate = DEFAULT_EFFORT_RATE,
    setDefaultEffortRate,
    manageMembers,
    goToPrevStep,
    goToStepFive,
    currentStep,
  } = props;
  const { _t } = useTranslator();
  const { loadUserTenant, user } = useContext(AuthContext);
  const { updateWorkspace, getWorkspaceMemberData } =
    useContext(LandLordContext);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState(null);
  const [editWorkspaceName, setEditWorkspaceName] = useState(workspace?.name);
  // Stores uploaded image preview URL
  const [uploadedImage, setUploadedImage] = useState(null);
  const [workspaceMemberInfo, setWorkspaceMemberInfo] = useState({});
  const [memberLoading, setMemberLoading] = useState(false);
  // Stores actual uploaded file (for backend)
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isModalButtonLoading, setIsModalButtonLoading] = useState(false);
  const imageInputRef = useRef(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [isEditingBeneficiary, setIsEditingBeneficiary] = useState(false);
  const [editDefaultEffortRate, setEditDefaultEffortRate] = useState(
    clampEffortRate(workspace?.default_effort_rate ?? DEFAULT_EFFORT_RATE)
  );
  const currentDefaultEffortRate =
    actionType === "edit" ? editDefaultEffortRate : defaultEffortRate;
  const canManageWorkspace =
    WORKSPACE_ROLES_CAPABILITIES?.manageWorkspace?.includes(
      user?.current_workspace_role
    );
  const canViewNotifications =
    WORKSPACE_ROLES_CAPABILITIES?.viewNotifications?.includes(
      user?.current_workspace_role
    );
  const isNotificationsOnlyRole =
    actionType === "edit" && !canManageWorkspace && canViewNotifications;
  const activeWorkspaceTab = isNotificationsOnlyRole
    ? "notifications"
    : workspaceTab;
  const isMinEffortRate = currentDefaultEffortRate <= MIN_EFFORT_RATE;
  const isMaxEffortRate = currentDefaultEffortRate >= MAX_EFFORT_RATE;

  useEffect(() => {
    if (actionType == "edit") {
      setEditWorkspaceName(workspace?.name);
      setEditDefaultEffortRate(
        clampEffortRate(workspace?.default_effort_rate ?? DEFAULT_EFFORT_RATE)
      );
    }
  }, [actionType, workspace]);

  const handleDefaultEffortRateChange = (change) => {
    const nextValue = clampEffortRate(currentDefaultEffortRate + change);

    if (actionType === "edit") {
      setEditDefaultEffortRate(nextValue);
    } else {
      setDefaultEffortRate?.(nextValue);
    }
  };

  useEffect(() => {
    loadMembersData();
  }, []);

  const loadMembersData = async () => {
    try {
      setMemberLoading(true);
      const memberData = await getWorkspaceMemberData();
      setWorkspaceMemberInfo(memberData);
    } catch (error) {
      setWorkspaceMemberInfo({});
    } finally {
      setMemberLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let value = e.target.value;

    // Prevent space as first character
    value = value.replace(/^\s+/, "");

    if (actionType == "edit") {
      setEditWorkspaceName(value);
    } else {
      setWorkspaceName(value);
    }
  };

  const handleNextStep = () => {
    if (haveValue(selectedAvatarIndex)) {
      setWorkspaceImage({ selectedAvatarIndex: selectedAvatarIndex });
    } else if (haveValue(uploadedFile)) {
      setWorkspaceImage({ uploadedFile: uploadedFile });
    }

    // setOpenInviteModal(true);
    setOpenInvitationCustomization(true);
    setOpenWorkspaceModal(false);
  };

  useEffect(() => {
    if (currentStep === 5) {
      console.log("inside", currentStep);
      handleNextStep();
      goToStepFive();
    }
  }, [currentStep]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setUploadedImage(URL.createObjectURL(file));
    setSelectedAvatarIndex(null);
    if (actionType == "edit") {
      handleSubmitEdit(null, null, file, false, false);
    }
  };
  const handleDefaultImage = () => {
    setSelectedAvatarIndex(null);
    setUploadedImage(null);
    setUploadedFile(null);
    if (actionType == "edit") {
      handleSubmitEdit(null, null, null, true, false);
    }
  };
  const handleAvatarSelect = (index) => {
    setSelectedAvatarIndex(index);
    setUploadedImage(null);
    setUploadedFile(null);
    if (actionType == "edit") {
      handleSubmitEdit(null, index, null, false, false);
    }
  };

  const handleSubmitEdit = async (
    event,
    index = null,
    fileUpload = null,
    isEmpty = false,
    isTextLoading = true
  ) => {
    try {
      setIsImageLoading(true);
      const tempData = {
        name: editWorkspaceName,
      };
      const formData = new FormData();
      formData.append("name", editWorkspaceName);
      formData.append("default_effort_rate", editDefaultEffortRate);
      if (haveValue(index)) {
        console.log("Inside index");
        formData.append("imageUrl", `DefaultWorkspace${index}.png`);
      } else if (haveValue(fileUpload)) {
        console.log("Inside fileUpload");
        formData.append("profileImage", fileUpload);
      } else if (isEmpty) {
        console.log("Inside  isEmpty");
        formData.append("imageUrl", null);
      } else {
        formData.append("imageUrl", workspace?.workspaceImage);
      }
      setIsModalButtonLoading(true);
      await updateWorkspace(formData);
      toast.success(_t("workspaceUpdatedSuccessfully"));
      setEditWorkspaceName(tempData?.name);
      await loadUserTenant("landlord");
    } catch (err) {
      console.log(err);

      toast.error(_t("errorWhileUpdatingWorkspace"));
    } finally {
      setIsModalButtonLoading(false);
      setIsImageLoading(false);
    }
  };

  const hasNoPicture =
    !uploadedImage &&
    !uploadedFile &&
    selectedAvatarIndex === null &&
    !haveValue(workspace?.workspaceImage);

  // Name, picture and effort rate: the same form when creating and editing.
  const settingsForm = (
    <div className="workspace-settings">
      <TextField
        id="workspace-name"
        name="name"
        label={_t("workspaceName")}
        placeholder={_t("enterWorkspaceName")}
        hint={_t("youCanUseYourOrganizationOrCompanyNameKeepItSimple")}
        value={actionType == "edit" ? editWorkspaceName : workspaceName}
        onChange={handleInputChange}
        required
      />

      <Field label={_t("profilePicture")} className="workspace-picture-field">
        <p className="workspace-settings__hint">
          {_t(
            "uploadAnImageOrPickAnEmojiForProfilePictureItWillShowUpInSidebarAndNotification"
          )}
        </p>
        <div className="workspace-picture">
          <div
            className={`workspace-picture__preview ${
              actionType !== "add" && hasNoPicture ? "is-letter" : ""
            }`}
          >
            {isImageLoading ? (
              <LoadingSpinner
                size={"24px"}
                color="#253837"
                borderWidth={1}
                className={"docs-loading-spinner"}
              />
            ) : uploadedImage ? (
              <img src={uploadedImage} alt="Uploaded workspace logo" />
            ) : selectedAvatarIndex !== null ? (
              <img
                src={`/images/DefaultWorkspace${selectedAvatarIndex}.png`}
                alt="Selected avatar"
              />
            ) : actionType == "add" ? (
              <img
                src="/images/default-img.svg"
                alt=""
                className="is-placeholder"
              />
            ) : haveValue(workspace?.workspaceImage) ? (
              <img src={`${workspace?.workspaceImage}`} alt="Selected avatar" />
            ) : (
              <span>{workspace?.name?.substring(0, 1)}</span>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            iconStart={<Icon name="upload" size={16} />}
            onClick={() => imageInputRef.current?.click()}
          >
            {_t("uploadLogo")}
          </Button>
          <FileInput
            ref={imageInputRef}
            accept="image/*"
            hidden
            onChange={handleImageUpload}
          />
        </div>

        <div className="workspace-avatars">
          {actionType == "edit" && (
            <RowButton
              type="button"
              className={`workspace-avatar workspace-avatar--letter ${
                hasNoPicture ? "is-selected" : ""
              }`}
              onClick={() => handleDefaultImage()}
            >
              {workspace?.name?.substring(0, 1)}
            </RowButton>
          )}
          {[1, 2, 3, 4, 5, 6, 7].map((index) => (
            <RowButton
              type="button"
              key={index}
              className={`workspace-avatar ${
                selectedAvatarIndex === index ? "is-selected" : ""
              }`}
              aria-pressed={selectedAvatarIndex === index}
              onClick={() => handleAvatarSelect(index)}
            >
              <img
                src={`/images/DefaultWorkspace${index}.png`}
                alt={`Default avatar ${index}`}
              />
            </RowButton>
          ))}
        </div>
      </Field>

      {shouldShowCapacityReport(workspace, user) && (
        <Field
          label={_t("defaultEffortRateToggler")}
          hint={_t(
            "defaultEffortRateTogglerDescriptionInWorkspaceSettingsPopup"
          )}
          className="workspace-effort-field"
        >
          <div className="workspace-stepper">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDefaultEffortRateChange(-1)}
              disabled={isMinEffortRate}
              aria-label={_t("decrease")}
            >
              <Icon name="minus" size={16} />
            </Button>
            <span className="workspace-stepper__value">
              {currentDefaultEffortRate}%
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDefaultEffortRateChange(1)}
              disabled={isMaxEffortRate}
              aria-label={_t("increase")}
            >
              <Icon name="plus" size={16} />
            </Button>
          </div>
        </Field>
      )}
    </div>
  );

  const isNameMissing =
    (!haveValue(workspaceName) && !haveValue(editWorkspaceName)) ||
    isModalButtonLoading;

  const continueButton = (
    <div data-tooltip-id="button-action-tooltip" className="workspace-continue">
      <Button
        size="lg"
        block
        disabled={isNameMissing}
        onClick={
          actionType === "add"
            ? () => {
                handleNextStep();
                goToStepFive?.();
              }
            : handleSubmitEdit
        }
      >
        {_t("continue")}
      </Button>
      {isNameMissing && <ButtonActionTooltip />}
    </div>
  );

  // tabs this person may open, in this order
  const workspaceTabs =
    actionType === "edit"
      ? [
          canManageWorkspace && { value: "settings", label: _t("settings") },
          WORKSPACE_ROLES_CAPABILITIES?.manageMembers?.includes(
            user?.current_workspace_role
          ) && { value: "members", label: _t("members") },
          WORKSPACE_ROLES_CAPABILITIES?.editInvitationLink?.includes(
            user?.current_workspace_role
          ) && { value: "invitations", label: _t("invitations") },
          canViewNotifications && {
            value: "notifications",
            label: _t("notifications"),
          },
          WORKSPACE_ROLES_CAPABILITIES?.manageBilling?.includes(
            user?.current_workspace_role
          ) && { value: "billing", label: _t("billing") },
        ].filter(Boolean)
      : [];
  const canDeleteWorkspace =
    actionType === "edit" &&
    WORKSPACE_ROLES_CAPABILITIES?.deleteWorkspace?.includes(
      user?.current_workspace_role
    );

  const saveButton = (
    <div data-tooltip-id="button-action-tooltip" className="workspace-continue">
      <Button
        size="lg"
        disabled={isNameMissing}
        loading={isModalButtonLoading}
        onClick={handleSubmitEdit}
      >
        {_t("saveChanges")}
      </Button>
      {isNameMissing && <ButtonActionTooltip />}
    </div>
  );

  return (
    <div>
      {actionType == "add" ? (
        <DsModal
          open={isOpen}
          onClose={() => {
            onClose();
            goToPrevStep?.();
          }}
          id="manage-member-walkthrough"
          size="md"
          align="start"
          title={_t("createNewWorkspace")}
          description={_t("createYourNewSharedWorkspaceWithLandlords")}
          actions={continueButton}
        >
          {settingsForm}
        </DsModal>
      ) : (
        <DsModal
          open={
            isOpen &&
            !isEditingBilling &&
            !isEditingBeneficiary &&
            !openDeleteModal
          }
          onClose={() => {
            onClose();
            goToPrevStep?.();
          }}
          id="manage-member-walkthrough"
          size="wide"
          align="start"
          title={_t("manageWorkspace")}
        >
          {workspaceTabs.length > 1 && (
            <Tabs
              scroll
              className="workspace-tabs"
              label={_t("manageWorkspace")}
              value={activeWorkspaceTab}
              onChange={changeWorkspaceTab}
              items={workspaceTabs}
            />
          )}
          {activeWorkspaceTab === "settings" &&
          (actionType !== "edit" || canManageWorkspace) ? (
            <>
              {settingsForm}
              <div className="ds-modal__actions ds-modal__actions--end">
                {canDeleteWorkspace && (
                  <Button
                    variant="danger"
                    size="lg"
                    className="ds-modal__actions-start"
                    iconStart={<Icon name="trash" size={18} />}
                    onClick={() => setOpenDeleteModal(true)}
                  >
                    {_t("deleteMyWorkspace")}
                  </Button>
                )}
                {saveButton}
              </div>
            </>
          ) : activeWorkspaceTab === "invitations" ? (
            <InvitationsCustomization
              workspaceData={workspace}
              onClose={onClose}
              onEditBeneficiary={() => setIsEditingBeneficiary(true)}
            />
          ) : activeWorkspaceTab === "notifications" ? (
            <NotificationCustomization
              workspaceMemberInfo={workspaceMemberInfo}
              memberLoading={memberLoading}
              loadMembersData={loadMembersData}
            />
          ) : activeWorkspaceTab === "billing" ? (
            <BillingCustomization
              onClose={onClose}
              onEditBilling={() => setIsEditingBilling(true)}
            />
          ) : (
            <ManageMembersModal manageMembers={manageMembers} />
          )}
        </DsModal>
      )}
      {openDeleteModal && (
        <ConfirmWorkspaceRemovalModal
          setOpenDeleteModal={setOpenDeleteModal}
          onCloseMain={onClose}
        />
      )}
      {isEditingBilling && (
        <EditBillingModal
          show={isEditingBilling}
          onClose={() => setIsEditingBilling(false)}
        />
      )}
      {isEditingBeneficiary && (
        <EditBeneficiaryModal
          show={isEditingBeneficiary}
          onClose={() => setIsEditingBeneficiary(false)}
        />
      )}
    </div>
  );
};

export default CreateNewWorkspaceModal;
